import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Typography } from '@/components/Typography/Typography';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { INVOICE_FILE_ACCEPT, INVOICE_FILE_MAX_BYTES } from '../../../const/const';
import { uploadInvoiceFile } from '../../../api/api';
import { DropZone } from '../../../styles';
import { LiaFileInvoiceSolid } from 'react-icons/lia';
import { COLORS } from '@/styles/colors';
import { LoaderNew } from '@/components-new/LoaderNew/LoaderNew';

type PropsType = {
  fileName: string;
  onUploaded: (result: { file_key: string; file_name: string }) => void;
  onError: (message: string) => void;
};

export const InvoiceDropZone = ({ fileName, onUploaded, onError }: PropsType) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);

  const upload = useMutation({
    mutationFn: uploadInvoiceFile,
    onSuccess(response) {
      if (response.error) return onError(getResponseError(response.error));

      onError('');
      onUploaded(response.body);
    },
  });

  const pick = (file?: File | null) => {
    if (!file) return;
    if (file.size > INVOICE_FILE_MAX_BYTES) return onError('The file is larger than 10 MB.');

    upload.mutate(file);
  };

  return (
    <DropZone
      $active={active}
      onDragOver={(e) => {
        e.preventDefault();
        setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setActive(false);
        pick(e.dataTransfer.files?.[0]);
      }}
    >
      <input ref={inputRef} type='file' accept={INVOICE_FILE_ACCEPT} onChange={(e) => pick(e.target.files?.[0])} />
      {upload.isPending ? (
        <LoaderNew />
      ) : fileName ? (
        <>
          <LiaFileInvoiceSolid size={50} color={COLORS.green[600]} />
          <Typography variant='body' fontWeight='medium'>
            {fileName}
          </Typography>
          <Typography variant='caption'>Click or drop another file to replace it.</Typography>
        </>
      ) : (
        <>
          <Typography fontWeight='medium'>Drop the invoice here or click to choose</Typography>
          <Typography variant='bodySmall2'>
            PDF, PNG or JPG(JPEG), up to 10 MB. Stored encrypted, never shown to the vendor.
          </Typography>
        </>
      )}
    </DropZone>
  );
};
